<script setup>
import { useI18n } from 'vue-i18n'
import ChipEventType from '@/components/ChipEventType.vue'
import ChipPoints from '@/components/ChipPoints.vue'
import { formatDate } from '@/lib/dates'
import { podiumColor } from '@/lib/podium'

/**
 * The results of one player in the events running right now.
 *
 * It only renders the list it is given (the API leaves out the events the player never entered), so
 * an empty list is the normal "not racing at the moment" state and gets a message instead of rows.
 * Each row is a link to the event.
 */
defineProps({
  /** Results in the running events, in the shape of GET /api/players/{username}. */
  events: { type: Array, default: () => [] },
})

const { t } = useI18n()
</script>

<template>
  <v-card elevation="3" rounded="lg" class="overflow-hidden">
    <div class="d-flex align-center ga-3 pa-4">
      <v-avatar color="primary" variant="tonal" size="48">
        <v-icon size="28">mdi-calendar-star</v-icon>
      </v-avatar>
      <div class="flex-grow-1">
        <h2 class="text-h6 font-weight-bold">{{ t('players.eventResults.title') }}</h2>
        <p class="text-caption text-medium-emphasis mb-0">{{ t('players.eventResults.subtitle') }}</p>
      </div>
      <v-chip v-if="events.length" variant="tonal" label>{{ events.length }}</v-chip>
    </div>

    <v-divider />

    <div v-if="!events.length" class="pa-6 text-center text-medium-emphasis">
      <v-icon size="36" class="d-block mx-auto mb-2">mdi-calendar-remove-outline</v-icon>
      {{ t('players.eventResults.empty') }}
    </div>

    <v-list v-else>
      <v-list-item
        v-for="event in events"
        :key="event.id"
        :to="{ name: 'event_detail', params: { id: event.id } }"
      >
        <template #prepend>
          <ChipEventType :type="event.eventType" class="mr-3" />
        </template>

        <v-list-item-title class="font-weight-medium">
          <!-- <v-icon size="14" class="mr-1">mdi-calendar-range</v-icon>
          {{ formatDate(event.dateStart) }} → {{ formatDate(event.dateEnd) }} -->
        </v-list-item-title>

        <template #append>
          <div class="d-flex align-center ga-2">
            <v-chip
              v-if="event.position !== null"
              size="small"
              variant="flat"
              label
              :color="podiumColor(event.position) ?? 'primary'"
            >
              {{ t('events.position') }} {{ event.position }}
            </v-chip>
            <ChipPoints v-if="event.points !== null" :points="event.points" />
            <v-chip v-else-if="event.resultTime" size="small" variant="tonal" label>
              {{ event.resultTime }}
            </v-chip>
          </div>
        </template>
      </v-list-item>
    </v-list>
  </v-card>
</template>
